import { useAppDispatch, useAppSelector } from "app/hooks";
import { AutoSelect, Button, Checkbox, DropDown, FrontBanner, Input, Loader, PlacesAutocomplete, Skeleton, TextArea } from 'components';
import { OptionListsType } from "components/AutoSelect";
import CheckboxItemsList, { CheckboxItemType } from "components/CheckboxItemsList";
import FormLabelBox from "components/FormLabelBox";
import MembershipDatePicker from "components/MembershipDatePicker";
import MembershipSelection from "components/MembershipSelection";
import PhoneNumberInput from "components/PhoneNumberInput";
import RadioItemsList, { RadioItemType } from "components/RadioItemsList";
import { useFormik } from 'formik';
import { Reccuring } from "helpers/subscriptions";
import { SubscriptionDetailsDto } from "models/subscriptions";
import Pricing from "pages/payment/components/Pricing";
import SubscriptionDetailInfo from "pages/payment/components/SubscriptionDetailInfo";
import SubscriptionsPlanList from "pages/payment/components/SubscriptionsPlanList";
import PaymentCard from "pages/payment/PaymentCard";
import React, { useEffect, useState } from 'react';
import { Country } from "react-phone-number-input";
import { profileActions } from "redux/reducers/profile";
import { selectCountries } from "redux/selectors/app";
import { selectProfile } from "redux/selectors/profile";
import { PRICE_ID, PROMO_CODE, SUBSCRIPTION_ID } from "utils/storageKeys";

type StepComponent = 'text' | 'date' | 'textarea' | 'select' | 'select-multiple' | 'radio' | 'radio-items' | 'radio-items-with-text' | 'checkbox' | 'autocomplete' | 'autocomplete-multiple' | 'places-autocomplete' | 'payment-card' | 'membership-selection' | 'calendar' | 'label' | 'phone-number' | 'checkbox-items';
interface StepField {
  label: string;
  sublabel?: string;
  secondaryLabel?: string;
  name: string;
  mask?: string;
  placeholder?: string;
  component: StepComponent;
  options?: any[];
  prefix?: string;
  showLabel?: boolean;
  otherTextField?: string;
  disabled?: boolean;
  format?: string;
  hideComponent?: boolean;
  optionsFetcher?: (state: any) => { key: string; value: string }[];
  fetchOptions?: () => Promise<{ key: string; value: string }[]>;
  defaultCountry?: Country;
}

interface DynamicStepProps {
  title: string;
  fields: StepField[];
  initialValues: Record<string, any>;
  validationSchema: any;
  onSubmit: (values: Record<string, any>) => void;
  next: () => void;
  isOnboardingFinish: boolean;
}

const DynamicStep: React.FC<DynamicStepProps> = ({ title, fields, initialValues, validationSchema, isOnboardingFinish, onSubmit, next }) => {
  const [searchValues, setSearchValues] = useState<Record<string, string>>({});
  const [options, setOptions] = useState<Record<string, { key: string; value: string, label?: string, textField?: string, description?: string, customValuePlaceholder?: string }[]>>({});
  const [loadingOptions, setLoadingOptions] = useState<Record<string, boolean>>({});
  const paymentCardRef = React.useRef<any>(null);
  const countries = useAppSelector(selectCountries);
  const user = useAppSelector(selectProfile);
  const [loading, setLoading] = useState(false);
  const isSubmitting = React.useRef(false);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const paymentCardField = fields.find(field => field.component === 'payment-card');
      if (paymentCardField && paymentCardRef.current) {
        const paymentMethodId = await paymentCardRef.current.processPayment();
        if (paymentMethodId) {
          await formik.setFieldValue(paymentCardField.name, paymentMethodId, true);
          const errors = await formik.validateForm();
          if (Object.keys(errors).length === 0) {
            onSubmit(values);
            next();
          }
        }
      } 
      else {
        onSubmit(values);
        next();
      }
    },
    validateOnBlur: false,
    validateOnChange: true,
    enableReinitialize: true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
      if (loading || isSubmitting.current) return;
    setLoading(true);
    isSubmitting.current = true;
    const paymentCardField = fields.find(field => field.component === 'payment-card');
    if (paymentCardField && paymentCardRef.current) {
      try {
        const paymentMethodId = await paymentCardRef.current.processPayment();
        if (paymentMethodId) {
          await formik.setFieldValue(paymentCardField.name, paymentMethodId, true);
          await new Promise(r => setTimeout(r, 0)); 
          const errors = await formik.validateForm();
          if (Object.keys(errors).length === 0) {
            const values = {
              ...formik.values,
              [paymentCardField.name]: paymentMethodId
            };
            onSubmit(values);
            next();
          }
        } else {
          const errors = await formik.validateForm();
          if (Object.keys(errors).length === 0) {
            onSubmit(formik.values);
            next();
          }
        }
      } catch (error) {
        console.error("Payment error:", error);
      } finally {
        isSubmitting.current = false;
        setLoading(false);
      }
    } else {
      try {
        await formik.handleSubmit();
      } finally {
        isSubmitting.current = false;
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fields.forEach(async (field) => {
      if (field.fetchOptions) {
        setLoadingOptions((prev) => ({ ...prev, [field.name]: true }));
        const fetchedOptions = await field.fetchOptions();
        setOptions((prev) => ({ ...prev, [field.name]: fetchedOptions }));
        setLoadingOptions((prev) => ({ ...prev, [field.name]: false }));
      }
    });
  }, [fields]);

  useEffect(() => {
    const initialSearchValues: Record<string, string> = {};
    fields.forEach((field) => {
      if (field.component === 'autocomplete' || field.component === 'autocomplete-multiple') {
        const value = initialValues[field.name];
        if (value) {
          initialSearchValues[field.name] = value.value || '';
        }
      }
    });
    setSearchValues(initialSearchValues);
  }, [initialValues, fields,]);

  const shouldShowError = (fieldName: string) => {
    return (formik.touched[fieldName] || formik.submitCount > 0) && Boolean(formik.errors[fieldName]);
  };

  return (
    <form onSubmit={handleSubmit} className="form-container">
      <FrontBanner mainText={title} />
      {fields.map((field) => {

        if(field.hideComponent) return null;
        switch (field.component) {
          case 'text':
            return (
              <Input
                key={field.name}
                id={field.name}
                name={field.name}
                type={'text'}
                label={field.label}
                prefix={field.prefix}
                mask={field?.mask}
                alwaysShowMask={false}
                placeholder={field?.placeholder}
                value={formik.values[field.name] || ''}
                onChange={formik.handleChange}
                onBlur={() => formik.setFieldTouched(field.name, true)}
                isError={shouldShowError(field.name)}
                errorText={formik.errors[field.name] as string}
                disabled={field.disabled}
              />
            );
          case 'textarea':{
            return (
              <TextArea
                key={field.name}
                id={field.name}
                name={field.name}
                label={field.label}
                secondaryLabel={field.secondaryLabel}
                value={formik.values[field.name] || ''}
                onChange={formik.handleChange}
                isError={shouldShowError(field.name)}
                errorText={formik.errors[field.name] as string}
              />
            );}
          case 'date':
            return (<Input
              key={field.name}
              id={field.name}
              name={field.name}
              type={'text'}
              label={field.label}
              placeholder={field?.placeholder}
              mask={field?.mask}
              alwaysShowMask={false}
              value={formik.values[field.name] || ''}
              onChange={formik.handleChange}
              onBlur={() => formik.setFieldTouched(field.name, true)}
              isError={shouldShowError(field.name)}
              errorText={formik.errors[field.name] as string}
            />)
          case 'select':
            return <DropDown
              type={'single'}
              key={field.name}
              label={field.label}
              id={field.name}
              name={field.name}
              value={formik.values[field.name] || ''}
              options={field.options || []}
              onChange={(option) => {
                formik.setFieldValue(field.name, option.value);
              }}
              isError={shouldShowError(field.name)}
              errorText={formik.errors[field.name] as string}
            >
            </DropDown>
           case 'select-multiple':{
            return <DropDown
              type={'multiple'}
              key={field.name}
              label={field.label}
              id={field.name}
              name={field.name}
              value={formik.values[field.name] || ''}
              options={field.options || []}
              onChange={(option) => {
                const currentValues = formik.values[field.name] || [];
                const newValues = currentValues.find((item: any) => item === option.value) ? currentValues : [...currentValues, option.value];
                formik.setFieldValue(field.name, newValues);
              }}
              onRemove={(option) => {
                const currentValues = formik.values[field.name] || [];
                const newValues = currentValues.filter((item: any) => item != option.value);
                formik.setFieldValue(field.name, newValues);
              }}
              isError={shouldShowError(field.name)}
              errorText={formik.errors[field.name] as string}
            >
            </DropDown>}
          case 'phone-number':
            return <PhoneNumberInput
              id={field.name}
              key={field.name}
              name={field.name}
              defaultCountry={field.defaultCountry}
              value={formik.values[field.name] || ''}
              onChange={(value) => {
                formik.setFieldValue(field.name, value);
              }} />
          case 'radio':
            return null;

          case 'checkbox':
            return null;

          case 'autocomplete':
          case 'autocomplete-multiple':
            const rawOptions = field.options || options[field.name] || [];
            const fieldOptions = rawOptions
              .map(opt => ({
                key: opt.key,
                value: opt.value,
                label: opt.value
              }))
            .filter(option => 
              option.value.toLowerCase().includes((searchValues[field.name] || '').toLowerCase())
            );
            const isLoading = loadingOptions[field.name];
            const isMultiple = field.component === 'autocomplete-multiple';
            
            const currentValue = formik.values[field.name];

            const currentSearchValue = searchValues[field.name] || '';

            return (
              <AutoSelect
                key={field.name}
                id={field.name}
                name={field.name}
                type={isMultiple ? 'multiple' : 'single'}
                label={field.label}
                searchValue={currentSearchValue}
                options={fieldOptions}
                value={currentValue}
                onValueChange={(value) => {
                  setSearchValues((prev) => ({ ...prev, [field.name]: value }));
                  if (!isMultiple) {
                    formik.setFieldValue(field.name, null);
                  }
                }}
                onSelect={(value) => {
                  if (isMultiple) {
                    const currentValues = formik.values[field.name] || [];
                    if (!currentValues.find((item: any) => item.key === value.key)) {
                      formik.setFieldValue(field.name, [...currentValues, value]);
                    } else {
                      formik.setFieldValue(
                        field.name,
                        currentValues.filter((item: any) => item.key !== value.key)
                      );
                    }
                    setSearchValues((prev) => ({ ...prev, [field.name]: '' }));
                  } else {
                    if (!value.value) {
                      formik.setFieldValue(field.name, null);
                    } else {
                      formik.setFieldValue(field.name, value);
                      setSearchValues((prev) => ({ ...prev, [field.name]: value.value }));
                    }
                  }
                }}
                isError={shouldShowError(field.name)}
                errorText={formik.errors[field.name] as string}
              />
            );

          case 'radio-items': {
            const optionsList = options[field.name] || field.options || [];
            const optionsListWithLabel = optionsList.map(option => ({
              ...option,
              label: option?.label || option.value,
            }));
            const selectedOption = optionsListWithLabel.find(opt => opt.value === formik.values[field.name]);
            
            const error =
            (formik.submitCount > 0 && selectedOption?.textField && formik.errors[selectedOption.textField]) ?
              (formik.errors[selectedOption.textField] as string)
              : (shouldShowError(field.name) ? (formik.errors[field.name] as string) : '');
        
            return (
              <>
              {field.showLabel && <span className="radio-items-label" ><div className='radio-items-label-link' dangerouslySetInnerHTML={{__html: field.label}} /></span>}
              <RadioItemsList
                key={field.name}
                options={optionsListWithLabel}
                value={formik.values[field.name] || ''}
                customValue={formik.values[selectedOption?.textField || '']}
                isCustomValue={!!selectedOption?.textField}
                customValuePlaceholder={selectedOption?.customValuePlaceholder || ''}
                onSelectItem={(value: string) => {
                  formik.setFieldValue(field.name, value);
                }}
                onCustomValueChange={(value: string) => {
                  if (selectedOption?.textField) {
                    formik.setFieldValue(selectedOption?.textField, value);
                  }
                }}
                isError={Boolean(error)}
                errorText={error as string}
                />
              </>
            );}
          case 'checkbox-items': {
            const optionsList = options[field.name] || field.options || [];
            const optionsListWithLabel = optionsList.map(option => ({
              ...option,
              label: option?.label || option.value,
            }));
            const selectedOption = optionsListWithLabel.find((opt: any) => 
              opt.value === formik.values[field.name]
            );
            
            const error = shouldShowError(field.name) ? (formik.errors[field.name] as string) : '';
        
            return (
              <>
              {field.showLabel && <span className="checkbox-items-label" ><div className='checkbox-items-label-link' dangerouslySetInnerHTML={{__html: field.label}} /></span>}
              <CheckboxItemsList
                key={field.name}
                options={optionsListWithLabel}
                value={formik.values[field.name] || ''}
                customValue={formik.values[selectedOption?.textField || '']}
                isCustomValue={!!selectedOption?.textField}
                onSelectItem={(value: string) => {
                  formik.setFieldValue(field.name, value);
                }}
                onCustomValueChange={(value: string) => {
                  if (selectedOption?.textField) {
                    formik.setFieldValue(selectedOption.textField, value);
                  }
                }}
                isError={Boolean(error)}
                errorText={error as string}
                />
              </>
            );}
          case 'radio-items-with-text': {
            const isLoading = loadingOptions[field.name];
            const optionsList = options[field.name] || field.options || [];
            const subscriptionId = sessionStorage.getItem(SUBSCRIPTION_ID);

            let optionsListWithLabel = optionsList.map(option => ({
              ...option,
              label: option.value
            }));

            if(subscriptionId) {
              optionsListWithLabel = optionsListWithLabel.filter(option => (option as any)?.id == subscriptionId);
            }

            const selectedSubscriptionInfo = optionsList.find(option => (option as any)?.id == formik.values[field.name].subscriptionId) || null as unknown as SubscriptionDetailsDto;

            if(isLoading){
              return <Loader />
            }
            const isError = (formik.errors[field.name] as unknown as {subscriptionId: string})?.subscriptionId;
            return (
              <div className="radio-items-membership-container">
                {selectedSubscriptionInfo && <Pricing
                    isOnlyAnnualAvailable={false}
                    passportFeeActive={true}
                    isAnnualSelected={formik.values[field.name]?.recurring == Reccuring.YEAR}
                    onPeriodChange={() => {
                      formik.setFieldValue(field.name,{ subscriptionId: formik.values[field.name].subscriptionId, recurring: formik.values[field.name].recurring == Reccuring.YEAR ? Reccuring.QUARTER : Reccuring.YEAR});
                    }}
                    selectedPlanPricing={(selectedSubscriptionInfo as unknown as SubscriptionDetailsDto)?.prices }
                  />}
              <SubscriptionsPlanList options={optionsListWithLabel} selected={selectedSubscriptionInfo as unknown as RadioItemType} onSelectItem={(value: any) => {
                formik.setFieldValue(field.name, { subscriptionId: value.id, recurring: formik.values[field.name].recurring});
              }} />
              {isError && <span className="input-label-error">{isError as string}</span>}
              <div className="payments-page-wrapper__desktop-block">
                {selectedSubscriptionInfo && <SubscriptionDetailInfo selectedSubscriptionInfo={selectedSubscriptionInfo as unknown as SubscriptionDetailsDto} />}
              </div>
              </div>
            );
          }

            case 'places-autocomplete':{
              return (
                <PlacesAutocomplete
                  id={field.name}
                  key={field.name}
                  name={field.name}
                  label={field.label}
                  value={formik.values[field.name] as string}
                  onSelect={(option: any) => {
                    formik.setFieldValue(field.name, option);
                  }}
                  onCountrySelect={(country: string) => {
                    // TODO: think how to add this to onbording steps
                    // formik.setFieldValue('countryOfResidence', {value: country, key: country});
                  }}
                  onBlur={(option: any) => formik.setFieldValue(field.name, option)}
                  isError={shouldShowError(field.name)}
                  errorText={formik.errors[field.name] as string}
                />
            );}
            case 'calendar':{
              return <MembershipDatePicker
                onChange={(value: Date) => {
                  formik.setFieldValue(field.name, value);
                }}
                label={field.label}
                value={formik.values[field.name] as Date}
                isError={shouldShowError(field.name)}
                errorText={formik.errors[field.name] as string}
                format={field.format}
              />
            }
            case 'label':{
              return <FormLabelBox
                label={field.label}
                sublabel={field.sublabel}
                value={formik.values[field.name]}
                description={''}
              />
            }
            case 'membership-selection':{
              const rawOptions = options[field.name] || field.options || [];
              let optionsList = rawOptions.map((option: any) => ({
                id: option.id || option.key,
                name: option.name || option.value,
                prices: option.prices || [],
                features: option.features || [],
                featuresAsterisk: option.featuresAsterisk || [],
                isDefault: option.isDefault || false,
                promoCode: option.promoCode || '',
                description: option.description || '',
                isArchived: option.isArchived || false,
                joiningFee: option.joiningFee || 0,
                benefitsLink: option.benefitsLink || '',
                onboardingAvailableIntervals: option.onboardingAvailableIntervals || [],
              })) as SubscriptionDetailsDto[];

              const subscriptionId = user?.profile?.membershipPreference?.subscriptionId || sessionStorage.getItem(SUBSCRIPTION_ID) || '';
              const priceId = user?.profile?.membershipPreference?.priceId || sessionStorage.getItem(PRICE_ID) || '';
              
              let selectedSubscriptionInfo = optionsList.find(option => option.id === formik.values[field.name].subscriptionId) || null;
              let selectedPrice = selectedSubscriptionInfo?.prices?.find(price => price.id === formik.values[field.name].priceId) || selectedSubscriptionInfo?.prices[0] || null;

              if (subscriptionId) {
                if (user?.profile?.membershipPreference?.isSubscriptionLocked) {
                  const lockedSubscription = optionsList.find(x => x.id === user?.profile?.membershipPreference?.subscriptionId);
                  if (lockedSubscription){
                    lockedSubscription.prices = lockedSubscription.onboardingAvailableIntervals?.length > 0
                      ? lockedSubscription.prices.filter(price => lockedSubscription.onboardingAvailableIntervals?.includes(price.reccuring))
                      : lockedSubscription.prices;
                    optionsList = [lockedSubscription]
                  }

                  // selectedSubscriptionInfo = optionsList.find(option => option.id === subscriptionId) || null;
                  // if (selectedSubscriptionInfo) {
                  //   selectedSubscriptionInfo.prices = selectedSubscriptionInfo.prices.filter(price => selectedSubscriptionInfo?.onboardingAvailableIntervals?.includes(price.reccuring));
                  //   optionsList = [selectedSubscriptionInfo];
                  // }
                } 
              }

              if (priceId) {
                const subscriptionByPriceId = optionsList.find(option => option.prices.some(p => p.id === priceId));
                if (user?.profile?.membershipPreference?.isPriceLocked) {
                if (subscriptionByPriceId) {
                    selectedSubscriptionInfo = subscriptionByPriceId;
                    selectedPrice = subscriptionByPriceId.prices.find(p => p.id === priceId) || null;
                    optionsList = [selectedSubscriptionInfo];
                    if (selectedPrice) {
                      optionsList[0].prices = [selectedPrice];
                    }
                  }
                }
              }

              if (!user?.profile?.membershipPreference?.isSubscriptionLocked && !user?.profile?.membershipPreference?.isPriceLocked) {
                optionsList = optionsList.filter(option => !option.isArchived);
                optionsList.forEach(option => {
                  if (option.onboardingAvailableIntervals?.length > 0){
                    option.prices = option.prices.filter(price => option.onboardingAvailableIntervals?.includes(price.reccuring));
                  }
                });
              }

              return (
                loadingOptions?.['membershipPreference'] ? (
                  <div className="membership-wrapper">
                    <Skeleton className="membership-price-skeleton" />
                    <Skeleton className="membership-price-skeleton" />
                  </div>
                ) :
                <MembershipSelection
                  options={optionsList}
                  selectedSubscription={selectedSubscriptionInfo}
                  selectedPrice={selectedPrice}
                  onSelectSubscription={(subscription) => {
                    if (user?.profile?.membershipPreference?.isSubscriptionLocked || user?.profile?.membershipPreference?.isPriceLocked) return;
                    if (subscription.id === formik.values[field.name].subscriptionId){
                      formik.setFieldValue(field.name, { 
                        subscriptionId: '', 
                        recurring: '',
                        priceId: ''
                      });
                    } else {
                      formik.setFieldValue(field.name, { 
                        subscriptionId: subscription.id, 
                        recurring: formik.values[field.name].recurring,
                        priceId: subscription.prices[0]?.id
                      });
                    }
                  }}
                  onSelectPrice={(price) => {
                    if (user?.profile?.membershipPreference?.isPriceLocked) return;
                    formik.setFieldValue(field.name, { 
                      ...formik.values[field.name],
                      priceId: price.id,
                      recurring: price.reccuring
                    });
                  }}
                  isError={shouldShowError(field.name)}
                  errorText={formik.errors[field.name] as string}
                />
              );
            }

            case 'payment-card': {
              return (
                <div className="payment-card-container">
                <PaymentCard 
                  ref={paymentCardRef}
                  onSubmit={(values: any) => {
                    formik.setFieldValue(field.name, values);
                  }} 
                  isError={Boolean(formik.errors[field.name])}
                  errorText={loading ? "" : formik.errors[field.name] as string}
                />
                </div>
              )
            }
          default:
            return null;
        }
      })}
      <Button type="submit" text="Next" loading={loading || isOnboardingFinish} disabled={loading || isOnboardingFinish} />
    </form>
  );
};

export default DynamicStep;